import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, AppWindow, Megaphone, MessageSquare, User, MessageCircle, Info, FlaskConical, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { updatePageAppCapabilities } from '@/services/api/connections';
import type {
    MetaPage,
    MetaPageAppLink,
    PageTokenCapability,
} from '../types/meta.types';

interface CapDef {
    key: PageTokenCapability;
    label: string;
    description: string;
    icon: any;
    /** Quais meta_modes têm essa capability disponível. */
    availableIn: Array<'dev' | 'production'>;
}

const ALL_CAPS: CapDef[] = [
    {
        key: 'broadcast',
        label: 'Envio de Broadcasts',
        description: 'Pode disparar broadcasts em massa por esse app.',
        icon: Megaphone,
        availableIn: ['dev', 'production'],
    },
    {
        key: 'flow',
        label: 'Execução de Flow',
        description: 'Responde mensagens, postbacks, auto-replies via flow.',
        icon: MessageSquare,
        availableIn: ['dev', 'production'],
    },
    {
        key: 'lead_profile',
        label: 'Captura de Nome do Lead',
        description: 'Usa Profile API pra buscar nome quando o lead manda 1ª mensagem. Apps em Dev só conseguem ler nome de testers.',
        icon: User,
        availableIn: ['production'],
    },
    {
        key: 'comment_moderation',
        label: 'Moderação de Comentários',
        description: 'Curtir, responder, ocultar ou deletar comentários no feed. Apps em Dev não têm permissão pra moderar comentários de não-testers.',
        icon: MessageCircle,
        availableIn: ['production'],
    },
];

/** Capabilities disponíveis pra um app baseado no modo dele. */
const getAvailableCaps = (mode: 'dev' | 'production'): CapDef[] =>
    ALL_CAPS.filter(c => c.availableIn.includes(mode));

/** Capabilities default quando o vínculo ainda não foi configurado (NULL no DB).
 *  - Production: todas
 *  - Dev: só broadcast (flow funciona só pra testers, então fica desmarcado
 *    por padrão; user pode marcar manualmente se quiser usar pro flow). */
const defaultCapsForMode = (mode: 'dev' | 'production'): PageTokenCapability[] => {
    if (mode === 'dev') return ['broadcast'];
    return getAvailableCaps(mode).map(c => c.key);
};

interface PageAppCapabilitiesPanelProps {
    page: MetaPage;
}

/**
 * Filtra os apps conectados da página pra mostrar só os que fazem sentido
 * pra esta página configurar:
 *  - Sem grupo: mostra todos os apps conectados
 *  - Com grupo: mostra app do grupo (dev) + apps em Production (que lidam
 *    com sub-tasks como lead_profile/comment_moderation). Outros apps dev
 *    que estejam conectados mas que pertencem a OUTROS grupos ficam de fora
 *    — eles servem outras páginas.
 */
const filterRelevantApps = (page: MetaPage, apps: MetaPageAppLink[]): MetaPageAppLink[] => {
    const hasGroup = page.internal_group_id != null;
    if (!hasGroup) return apps;
    const groupAppId = page.internal_group_app_id;
    return apps.filter(a =>
        a.app_id_pk === groupAppId || a.meta_mode === 'production'
    );
};

/** Resolve quais caps estão habilitadas considerando o default-por-mode. */
const resolveEnabledCaps = (link: MetaPageAppLink): Set<PageTokenCapability> => {
    if (link.capabilities == null) {
        return new Set(defaultCapsForMode(link.meta_mode));
    }
    // Limita ao que está disponível no modo (defesa: se vier com lead_profile
    // num app dev por algum motivo, ignora — ele não conseguiria executar).
    const available = new Set(getAvailableCaps(link.meta_mode).map(c => c.key));
    return new Set(link.capabilities.filter(c => available.has(c)));
};

export default function PageAppCapabilitiesPanel({ page }: PageAppCapabilitiesPanelProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    // Apps relevantes (filtrados por grupo). Apps que estão conectados mas
    // pertencem a outros grupos ficam invisíveis aqui — esta tela é só pros
    // que servem essa página.
    const allApps = page.apps || [];
    const apps = useMemo(() => filterRelevantApps(page, allApps), [page, allApps]);
    const hiddenAppsCount = allApps.length - apps.length;

    // Estado local: por app_id_pk → set de capabilities marcadas.
    // Default smart: vínculo NULL no banco resolve por meta_mode.
    type State = Record<number, Set<PageTokenCapability>>;
    const [state, setState] = useState<State>(() => {
        const next: State = {};
        apps.forEach(link => {
            next[link.app_id_pk] = resolveEnabledCaps(link);
        });
        return next;
    });

    // Sync quando page.apps mudar
    useEffect(() => {
        const next: State = {};
        apps.forEach(link => {
            next[link.app_id_pk] = resolveEnabledCaps(link);
        });
        setState(next);
        // apps é derivado de page.apps; dependência em page.apps é suficiente
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page.apps]);

    const dirty = useMemo(() => {
        return apps.some(link => {
            const current = state[link.app_id_pk] || new Set<PageTokenCapability>();
            const resolved = resolveEnabledCaps(link);
            const available = getAvailableCaps(link.meta_mode).map(c => c.key);
            return available.some(key => current.has(key) !== resolved.has(key));
        });
    }, [state, apps]);

    const toggle = (appPk: number, cap: PageTokenCapability) => {
        setState(prev => {
            const set = new Set(prev[appPk] || []);
            if (set.has(cap)) set.delete(cap);
            else set.add(cap);
            return { ...prev, [appPk]: set };
        });
    };

    const saveMutation = useMutation({
        mutationFn: async () => {
            const updates = apps.map(link => ({
                app_id_pk: link.app_id_pk,
                capabilities: Array.from(state[link.app_id_pk] || []),
            }));
            return updatePageAppCapabilities(page.id, updates);
        },
        onSuccess: (r) => {
            toast({ title: 'Funções salvas!', description: `${r.updated} vínculo(s) atualizado(s).` });
            queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
            queryClient.invalidateQueries({ queryKey: ['meta-page', page.id] });
        },
        onError: (e: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: e?.response?.data?.detail || 'Não foi possível salvar.',
            });
        },
    });

    return (
        <div className="space-y-5">
            <div className="space-y-1">
                <h2 className="text-lg font-bold text-gray-900">Funções por App</h2>
                <p className="text-sm text-gray-600">
                    Define quais funções cada app conectado é autorizado a exercer pra esta página.
                    Útil pra separar: ex. apps dev só fazem envios, app de produção captura nomes e
                    modera comentários.
                </p>
            </div>

            {/* Aviso quando há apps escondidos por filtro de grupo */}
            {hiddenAppsCount > 0 && page.internal_group_name && (
                <Alert className="border-gray-200 bg-gray-50">
                    <Info className="h-4 w-4 text-gray-500" />
                    <AlertDescription className="text-xs text-gray-600">
                        Esta página está no grupo <strong>{page.internal_group_name}</strong> (app{' '}
                        <strong>{page.internal_group_app_name}</strong>). {hiddenAppsCount} outro{hiddenAppsCount !== 1 ? 's' : ''}{' '}
                        app{hiddenAppsCount !== 1 ? 's' : ''} dev{hiddenAppsCount !== 1 ? 's' : ''}{' '}
                        que tem token aqui {hiddenAppsCount !== 1 ? 'estão' : 'está'} oculto{hiddenAppsCount !== 1 ? 's' : ''}{' '}
                        porque serve{hiddenAppsCount !== 1 ? 'm' : ''} outras páginas. Pra mudar isso, troque o grupo da página
                        em <strong>Configurações → Grupos de Páginas</strong>.
                    </AlertDescription>
                </Alert>
            )}

            {apps.length === 0 ? (
                <Alert className="border-amber-200 bg-amber-50">
                    <Info className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-sm text-amber-800">
                        Esta página não tem nenhum app conectado. Conecte primeiro pela aba "Contas e Usuários".
                    </AlertDescription>
                </Alert>
            ) : (
                <div className="space-y-3">
                    {apps.map(link => {
                        const current = state[link.app_id_pk] || new Set<PageTokenCapability>();
                        const availableCaps = getAvailableCaps(link.meta_mode);
                        const allOn = availableCaps.every(c => current.has(c.key));
                        const allOff = availableCaps.every(c => !current.has(c.key));
                        return (
                            <div
                                key={link.app_id_pk}
                                className="rounded-xl border border-gray-200 bg-white p-4 space-y-3"
                            >
                                {/* App header */}
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="h-9 w-9 rounded-lg bg-gray-50 flex items-center justify-center border border-gray-100 shrink-0">
                                            <AppWindow className="h-4.5 w-4.5 text-gray-500" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="text-sm font-semibold text-gray-900 truncate">
                                                    {link.app_name}
                                                </p>
                                                {link.meta_mode === 'dev' ? (
                                                    <Badge variant="outline" className="text-[10px] py-0 h-4 border-amber-200 bg-amber-50/50 text-amber-700">
                                                        <FlaskConical className="h-2.5 w-2.5 mr-1" />
                                                        Dev
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-[10px] py-0 h-4 border-blue-200 bg-blue-50/50 text-blue-700">
                                                        <Rocket className="h-2.5 w-2.5 mr-1" />
                                                        Production
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span className="text-[10px] text-gray-400 font-mono">
                                                    {link.app_id}
                                                </span>
                                                {link.token_status === 'ok' ? (
                                                    <Badge variant="outline" className="text-[10px] py-0 h-4 border-emerald-200 bg-emerald-50/50 text-emerald-700">
                                                        token OK
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-[10px] py-0 h-4 border-amber-200 bg-amber-50/50 text-amber-700">
                                                        {link.token_status || 'sem token'}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex gap-3 text-xs">
                                        <button
                                            type="button"
                                            disabled={allOn}
                                            onClick={() =>
                                                setState(prev => ({
                                                    ...prev,
                                                    [link.app_id_pk]: new Set(availableCaps.map(c => c.key)),
                                                }))
                                            }
                                            className="text-blue-600 hover:underline disabled:text-gray-300 disabled:no-underline"
                                        >
                                            Marcar todas
                                        </button>
                                        <span className="text-gray-300">|</span>
                                        <button
                                            type="button"
                                            disabled={allOff}
                                            onClick={() =>
                                                setState(prev => ({
                                                    ...prev,
                                                    [link.app_id_pk]: new Set(),
                                                }))
                                            }
                                            className="text-gray-500 hover:underline disabled:text-gray-300 disabled:no-underline"
                                        >
                                            Limpar
                                        </button>
                                    </div>
                                </div>

                                {link.meta_mode === 'dev' && (
                                    <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-1.5">
                                        App em <strong>Dev mode</strong> só consegue exercer envios. Captura de nome
                                        e moderação de comentário requerem app em Production aprovado pelo Meta.
                                    </p>
                                )}

                                {/* Capabilities — só as disponíveis pro modo do app */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-gray-100">
                                    {availableCaps.map(({ key, label, description, icon: Icon }) => {
                                        const checked = current.has(key);
                                        return (
                                            <label
                                                key={key}
                                                className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                                                    checked
                                                        ? 'border-blue-300 bg-blue-50/40'
                                                        : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                            >
                                                <Checkbox
                                                    checked={checked}
                                                    onCheckedChange={() => toggle(link.app_id_pk, key)}
                                                    className="mt-0.5"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5">
                                                        <Icon className="h-3.5 w-3.5 text-gray-500" />
                                                        <span className="text-sm font-medium text-gray-900">{label}</span>
                                                    </div>
                                                    <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                                                        {description}
                                                    </p>
                                                </div>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {apps.length > 0 && (
                <div className="flex items-center justify-end gap-2 pt-2">
                    <Button
                        onClick={() => saveMutation.mutate()}
                        disabled={!dirty || saveMutation.isPending}
                        className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                    >
                        <Save className="mr-2 h-4 w-4" />
                        {saveMutation.isPending ? 'Salvando...' : 'Salvar Alterações'}
                    </Button>
                </div>
            )}
        </div>
    );
}
